import useIntegrations from 'Hooks/useIntegrations';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { cn } from 'Utils/cn';
import TagsList from 'Components/Molecules/TagsList/TagsList';
import useCategories from 'Hooks/useCategories';

export default function ContactMergeDrawerItem(props) {
    const {
        name,
        className,
        checked,
        onChange,
        address,
        type,
        tax_id,
        city,
        state,
        postal_code,
        country,
        disabled,
        integration_id,
        categories_ids,
        categorization_keywords,
    } = props;

    const { integrations } = useIntegrations();
    const { t } = useTranslation('contacts');
    const icon = integrations.find((integration) => integration.id === integration_id)?.icon;
    const fullAddress = [address, postal_code, city, state, country].filter(Boolean).join(' ');
    const { categories, isLoading } = useCategories();

    return (
        <label
            className={cn(
                'inline-flex items-start cursor-pointer select-none gap-3 border border-gray-200 shadow-sm px-3 py-4 rounded-lg',
                className,
            )}
        >
            <div className="inline-flex size-5 relative shrink-0">
                <input
                    type="radio"
                    className={cn(
                        'peer bg-white appearance-none size-full border-1 border-gray-300 rounded-full shadow-sm transition-all cursor-pointer text-dark-blue',
                        'focus-visible:outline-none focus-visible:ring-2 ring-primary/20 focus-visible:border-primary',
                        'disabled:bg-gray-100 disabled:border-gray-200 disabled:cursor-not-allowed',
                    )}
                    onChange={onChange}
                    checked={checked}
                    disabled={disabled}
                />

                <span className="pointer-events-none absolute top-1/2 left-1/2 -translate-1/2 size-3 rounded-full peer-checked:bg-dark-blue peer-checked:scale-100 scale-0 transition-transform peer-disabled:bg-gray-600" />
            </div>

            <span className="grow pb-1">
                <div className="font-semibold text-dark-blue mb-3 text-base relative">
                    {name || '-'}
                    {icon && <img src={icon} alt="" className="inline-flex size-[14px] absolute mt-1 ml-0.5" />}
                </div>
                <div className="gap-x-4 gap-y-3 text-xs text-gray-500 grid grid-cols-[3fr_2fr] md:grid-cols-[3fr_2fr_2fr]">
                    <div>
                        <div className="text-2xs uppercase tracking-wider font-semibold mb-0.5">{t('address')}</div>
                        <div className="text-balance">{fullAddress || '...'}</div>
                    </div>
                    <div>
                        <div className="text-2xs uppercase tracking-wider font-semibold mb-0.5">{t('type')}</div>
                        <div className="text-balance">{type || '...'}</div>
                    </div>
                    <div>
                        <div className="text-2xs uppercase tracking-wider font-semibold mb-0.5">{t('tax-id')}</div>
                        <div className="text-balance">{tax_id || '...'}</div>
                    </div>
                </div>
                {categories_ids.length > 0 && (
                    <div className="py-1">
                        {!isLoading && <TagsList className="mt-2" tagIds={categories_ids} tags={categories} />}
                    </div>
                )}
                {categorization_keywords && categorization_keywords.length > 0 && (
                    <div className="mt-3">
                        <div className="text-2xs uppercase tracking-wider font-semibold mb-0.5 text-gray-500">
                            {t('linked-concepts')}
                        </div>
                        <div className="flex flex-wrap gap-1 mt-1">
                            {categorization_keywords.map((keyword) => (
                                <span
                                    key={keyword}
                                    className="bg-gray-100 text-gray-700 text-2xs px-2 py-1 rounded-full"
                                >
                                    {keyword}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
            </span>
        </label>
    );
}

ContactMergeDrawerItem.propTypes = {
    checked: PropTypes.bool,
    onChange: PropTypes.func,
    className: PropTypes.string,
    name: PropTypes.string,
    address: PropTypes.string,
    tax_id: PropTypes.string,
    type: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    postal_code: PropTypes.string,
    country: PropTypes.string,
    disabled: PropTypes.bool,
    integration_id: PropTypes.string,
    categories_ids: PropTypes.arrayOf(PropTypes.string),
    categorization_keywords: PropTypes.arrayOf(PropTypes.string),
};
